/**
 * The data model catalog of the current tenant (docs/catalog.md), loaded once before the app renders (like the
 * environments), so every `noun(cls)` in the app says a class by its display name from the first paint: the names go
 * into the registry of lib/nouns.ts. It lives inside the environment's subtree, so `inEnvironment` is about the
 * environment looked at. After an edit the registry changes at once and `version` grows, so the pages that use
 * the catalog render again.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Api } from './api.ts';
import { setCatalogName, setCatalogNames } from './nouns.ts';
import { newElsewhere, type CatalogData, type CatalogEntry, type CatalogStatus } from './catalog.ts';
import { useEnv } from './envs.tsx';
import type { Environment } from './env.ts';
import { Spinner } from '../components/ui.tsx';

interface CatalogState {
  /** By class. */
  entries: ReadonlyMap<string, CatalogEntry>;
  domains: string[];
  tags: string[];
  /** Grows with every change (load or edit). */
  version: number;
  reload: () => Promise<void>;
  /** Puts an edited entry in place (from the PATCH answer). */
  apply: (e: CatalogEntry) => void;
  /** Puts several edited entries in place (from the accept answer). */
  applyMany: (es: CatalogEntry[]) => void;
  /** Sets the status of classes (after a bulk change). */
  applyStatus: (classes: string[], status: CatalogStatus) => void;
}

const EMPTY: CatalogState = { entries: new Map(), domains: [], tags: [], version: 0, reload: async () => undefined, apply: () => undefined, applyMany: () => undefined, applyStatus: () => undefined };
const CatalogContext = createContext<CatalogState>(EMPTY);

export const useCatalog = (): CatalogState => useContext(CatalogContext);

const sortedSet = (xs: Iterable<string>) => [...new Set(xs)].sort((a, b) => a.localeCompare(b));

export function CatalogProvider({ tenantId, children }: { tenantId: string; children: ReactNode }) {
  const [data, setData] = useState<{ tid: string; entries: Map<string, CatalogEntry>; domains: string[]; tags: string[]; version: number }>();
  const [failed, setFailed] = useState<string>();
  const latest = useRef(0);

  const take = useCallback((tid: string, d: CatalogData) => {
    // The names first: the render this causes already says them
    setCatalogNames(d.classes);
    setData((prev) => ({ tid, entries: new Map(d.classes.map((e) => [e.class, e])), domains: d.domains, tags: d.tags, version: (prev?.version ?? 0) + 1 }));
  }, []);
  const reload = useCallback(async () => {
    const ticket = ++latest.current;
    try {
      const d = await Api.catalog(tenantId);
      if (ticket === latest.current) take(tenantId, d);
    } catch {
      // Without the catalog the app still works with the derived names
      if (ticket === latest.current) { setCatalogNames([]); setFailed(tenantId); }
    }
  }, [tenantId, take]);
  useEffect(() => {
    void reload();
    // New data models keep arriving: the count of those to check stays fresh
    const t = setInterval(() => { if (document.visibilityState === 'visible') void reload(); }, 60_000);
    return () => clearInterval(t);
  }, [reload]);

  const applyMany = useCallback((es: CatalogEntry[]) => {
    if (!es.length) return;
    for (const e of es) setCatalogName(e.class, e.displayName, e.displayPlural);
    setData((prev) => {
      if (!prev) return prev;
      const entries = new Map(prev.entries);
      for (const e of es) {
        const was = prev.entries.get(e.class);
        entries.set(e.class, { ...e, inEnvironment: e.inEnvironment ?? was?.inEnvironment, environments: e.environments ?? was?.environments });
      }
      const all = [...entries.values()];
      return { ...prev, entries, domains: sortedSet(all.map((x) => x.domain).filter((d): d is string => !!d)), tags: sortedSet(all.flatMap((x) => x.tags)), version: prev.version + 1 };
    });
  }, []);
  const apply = useCallback((e: CatalogEntry) => applyMany([e]), [applyMany]);
  const applyStatus = useCallback((classes: string[], status: CatalogStatus) => {
    setData((prev) => {
      if (!prev) return prev;
      const entries = new Map(prev.entries);
      for (const c of classes) { const e = entries.get(c); if (e) entries.set(c, { ...e, status, reviewedAt: status === 'new' ? null : Date.now() }); }
      return { ...prev, entries, version: prev.version + 1 };
    });
  }, []);

  const ready = data?.tid === tenantId || failed === tenantId;
  const value = useMemo<CatalogState>(() => (data?.tid === tenantId
    ? { entries: data.entries, domains: data.domains, tags: data.tags, version: data.version, reload, apply, applyMany, applyStatus }
    : { ...EMPTY, reload, apply, applyMany, applyStatus }), [data, tenantId, reload, apply, applyMany, applyStatus]);

  if (!ready) return <div className="min-h-screen bg-n1 grid place-items-center"><Spinner /></div>;
  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

/** How many classes of the environment looked at are new (the badge of Settings → Data model). */
export function useNewCount(): number {
  const { entries } = useCatalog();
  return useMemo(() => [...entries.values()].filter((e) => e.inEnvironment && e.status === 'new').length, [entries]);
}

/** New data models of the other environments that the one looked at does not have, per environment (in the switcher's order). */
export function useNewElsewhere(): { env: Environment; count: number }[] {
  const { entries } = useCatalog();
  const { envs, current } = useEnv();
  return useMemo(() => newElsewhere(entries.values(), current?.id, envs.map((e) => e.id))
    .map((x) => ({ env: envs.find((e) => e.id === x.envId)!, count: x.count })), [entries, envs, current]);
}
